import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { BookOpen, Info, MessageSquareWarning, Send, X } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { getStoredAccountType } from "../auth";
import { FAQ, MANUAL_URL } from "../data/helpContent";
import { fetchMySupportRequests, sendSupportRequest, supportDate, type SupportRequest } from "../api/supportApi";
import { FormField, inputClass, selectClass } from "./FormField";

type Tab = "faq" | "manual" | "request";

const TABS: Array<{ id: Tab; label: string }> = [
  { id: "faq", label: "FAQ" },
  { id: "manual", label: "Manuel d'utilisation" },
  { id: "request", label: "Plainte / requête" },
];

/** Le manuel n'est proposé que si le PDF a été déposé (sinon le serveur renvoie la page de l'application). */
function useManualAvailable(open: boolean): boolean | null {
  const [available, setAvailable] = useState<boolean | null>(null);
  useEffect(() => {
    if (!open || available !== null) return;
    fetch(MANUAL_URL, { method: "HEAD" })
      .then((response) => setAvailable(response.ok && (response.headers.get("content-type") ?? "").includes("pdf")))
      .catch(() => setAvailable(false));
  }, [open, available]);
  return available;
}

function RequestTab() {
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [form, setForm] = useState({ category: "requete" as SupportRequest["category"], subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);

  const load = () => fetchMySupportRequests().then(setRequests).catch(() => setRequests([]));
  useEffect(() => {
    void load();
  }, []);

  const submit = async () => {
    setSending(true);
    setNotice(null);
    try {
      await sendSupportRequest({ ...form, subject: form.subject.trim(), message: form.message.trim() });
      setForm({ category: form.category, subject: "", message: "" });
      setNotice({ ok: true, text: "Message envoyé aux administrateurs. Leur réponse apparaîtra ci-dessous." });
      void load();
    } catch (err) {
      setNotice({ ok: false, text: err instanceof Error ? err.message : "Envoi impossible." });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <FormField label="Type">
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as SupportRequest["category"] })} className={selectClass}>
            <option value="requete">Requête</option>
            <option value="plainte">Plainte</option>
          </select>
        </FormField>
        <FormField label="Objet" required className="sm:col-span-2">
          <input value={form.subject} maxLength={150} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputClass} />
        </FormField>
        <FormField label="Message" required className="sm:col-span-3">
          <textarea value={form.message} rows={4} maxLength={5000} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputClass} h-auto py-2`} />
        </FormField>
      </div>
      <div className="flex items-center justify-between gap-3">
        {notice ? <p className={cn("text-sm", notice.ok ? "text-emerald-700" : "text-red-600")}>{notice.text}</p> : <span />}
        <button
          type="button"
          disabled={sending || !form.subject.trim() || !form.message.trim()}
          onClick={() => void submit()}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
        >
          <Send size={15} aria-hidden="true" />
          Envoyer aux administrateurs
        </button>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Mes envois</p>
        {requests.length === 0 ? (
          <p className="text-sm text-muted">Aucun envoi pour l'instant.</p>
        ) : (
          <ul className="flex max-h-64 flex-col gap-2 overflow-y-auto">
            {requests.map((request) => (
              <li key={request.id} className="rounded-lg border border-border p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-foreground">{request.category_label} · {request.subject}</span>
                  <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", request.status === "open" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700")}>
                    {request.status_label}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{supportDate(request.created_at)}</p>
                {request.reply ? (
                  <p className="mt-2 rounded-md bg-card-muted px-2 py-1.5 text-foreground">
                    <span className="font-medium">Réponse{request.replied_by ? ` de ${request.replied_by}` : ""} :</span> {request.reply}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Bouton « i » du menu : FAQ, manuel d'utilisation et plainte / requête aux Admin. */
export function HelpCenter({ expanded }: { expanded: boolean }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("faq");
  const manualAvailable = useManualAvailable(open);
  const isAdmin = ["admin", "super_admin"].includes(getStoredAccountType() ?? "");

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Aide : FAQ, manuel et plainte"
        title="Aide"
        className={cn(
          "mt-3 flex shrink-0 items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-sidebar-muted transition-colors hover:bg-sidebar-hover hover:text-white",
          expanded ? "w-full" : "justify-center",
        )}
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-white/20" aria-hidden="true">
          <Info size={16} />
        </span>
        {expanded ? "Aide et contact" : null}
      </button>

      {open
        ? createPortal(
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Aide"
                onClick={(event) => event.stopPropagation()}
                className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-card shadow-xl"
              >
                <div className="flex items-center justify-between border-b border-border px-5 py-4">
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                    <Info size={18} className="text-primary" aria-hidden="true" />
                    Aide
                  </h2>
                  <button type="button" onClick={() => setOpen(false)} aria-label="Fermer" className="rounded-lg p-1.5 text-muted hover:bg-card-muted">
                    <X size={18} />
                  </button>
                </div>
                <div className="flex gap-1 border-b border-border px-5 pt-3">
                  {TABS.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setTab(item.id)}
                      className={cn(
                        "-mb-px rounded-t-lg border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                        tab === item.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
                <div className="overflow-y-auto p-5">
                  {tab === "faq" ? (
                    <div className="flex flex-col gap-2">
                      {FAQ.map((item) => (
                        <details key={item.question} className="rounded-lg border border-border px-3 py-2">
                          <summary className="cursor-pointer text-sm font-medium text-foreground">{item.question}</summary>
                          <p className="mt-2 text-sm text-muted-foreground">{item.answer}</p>
                        </details>
                      ))}
                    </div>
                  ) : tab === "manual" ? (
                    <div className="flex flex-col items-start gap-3 text-sm">
                      <BookOpen size={28} className="text-primary" aria-hidden="true" />
                      {manualAvailable ? (
                        <a href={MANUAL_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 font-medium text-white hover:bg-accent hover:text-accent-foreground">
                          Ouvrir le manuel (PDF)
                        </a>
                      ) : (
                        <p className="text-muted-foreground">
                          {manualAvailable === null ? "Recherche du manuel…" : "Le manuel d'utilisation est en cours de rédaction par l'équipe IT. Il sera disponible ici."}
                        </p>
                      )}
                    </div>
                  ) : isAdmin ? (
                    <div className="flex flex-col items-start gap-3 text-sm">
                      <MessageSquareWarning size={28} className="text-primary" aria-hidden="true" />
                      <p className="text-muted-foreground">Les plaintes et requêtes des utilisateurs arrivent dans votre espace Admin.</p>
                      <Link to="/dashboard/support-requests" onClick={() => setOpen(false)} className="font-medium text-primary hover:underline">
                        Voir les plaintes et requêtes reçues
                      </Link>
                    </div>
                  ) : (
                    <RequestTab />
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
